import React, { useEffect } from 'react';
import { st, classes } from './BaseModalLayout.st.css';
import { labels, dataHooks } from './constants';
import CloseButton from '../CloseButton';
import { Help24 as Help } from '@wix/wix-ui-icons-common/system';
import { ThemeProviderConsumerBackwardCompatible } from '../ThemeProvider/ThemeProviderConsumerBackwardCompatible';
import { BaseModalLayoutContext } from './BaseModalLayoutContext';
import {
  Header,
  Content,
  Footer,
  Footnote,
  Illustration,
} from './LayoutBlocks';
import Box from '../Box';
import { BaseModalLayoutProps } from './BaseModalLayout.types';
import deprecationLog from '../utils/deprecationLog';

const classNames = {
  headerClassName: classes.header,
  contentClassName: classes.content,
  footerClassName: classes.footer,
  footnoteClassName: classes.footnote,
  illustrationClassName: classes.illustration,
};

const BaseModalLayout = ({
  dataHook,
  className,
  children,
  style,
  onCloseButtonClick, // deprecated
  onHelpButtonClick, // deprecated
  closeButtonProps,
  helpButtonProps,
  skin = 'standard',
  ...restProps
}: BaseModalLayoutProps) => {
  useEffect(() => {
    if (onCloseButtonClick) {
      deprecationLog(
        '<BaseModalLayout /> - prop "onCloseButtonClick" is deprecated and will be removed in next major release, please use "closeButtonProps" property instead.',
      );
    }

    if (onHelpButtonClick) {
      deprecationLog(
        '<BaseModalLayout /> - prop "onHelpButtonClick" is deprecated and will be removed in next major release, please use "helpButtonProps" property instead.',
      );
    }
  }, [onCloseButtonClick, onHelpButtonClick]);

  helpButtonProps = {
    ...helpButtonProps,
    onClick: helpButtonProps?.onClick || onHelpButtonClick,
    skin: helpButtonProps?.skin || 'dark',
    size: helpButtonProps?.size || 'large',
  };

  closeButtonProps = {
    ...closeButtonProps,
    onClick: closeButtonProps?.onClick || onCloseButtonClick,
    skin: closeButtonProps?.skin || 'dark',
    size: closeButtonProps?.size || 'large',
  };

  const controlButtonAmount = [
    helpButtonProps?.onClick,
    closeButtonProps?.onClick,
  ].filter(Boolean).length;

  return (
    <div
      data-hook={dataHook}
      data-skin={skin}
      style={style}
      className={st(classes.root, { skin, controlButtonAmount }, className)}
    >
      {controlButtonAmount > 0 && (
        <ThemeProviderConsumerBackwardCompatible
          defaultIcons={{
            BaseModalLayout: {
              HelpIcon: Help,
            },
          }}
        >
          {({
            icons: {
              BaseModalLayout: { HelpIcon },
            },
          }) => (
            <Box direction="horizontal" className={classes.controlButtons}>
              {helpButtonProps.onClick && (
                <CloseButton
                  dataHook={dataHooks.helpButton}
                  className={classes.helpButton}
                  onClick={helpButtonProps.onClick}
                  onMouseDown={helpButtonProps.onMouseDown}
                  size={helpButtonProps.size}
                  skin={helpButtonProps.skin}
                  aria-label={labels.help}
                >
                  <HelpIcon className={classes.helpIcon} />
                </CloseButton>
              )}
              {closeButtonProps.onClick && (
                <CloseButton
                  dataHook={dataHooks.closeButton}
                  className={classes.closeButton}
                  onClick={closeButtonProps.onClick}
                  onMouseDown={closeButtonProps.onMouseDown}
                  size={closeButtonProps.size}
                  skin={closeButtonProps.skin}
                  autoFocus={closeButtonProps.autoFocus}
                  aria-label={labels.close}
                />
              )}
            </Box>
          )}
        </ThemeProviderConsumerBackwardCompatible>
      )}

      <BaseModalLayoutContext.Provider
        value={{ ...restProps, skin, ...classNames }}
      >
        {children}
      </BaseModalLayoutContext.Provider>
    </div>
  );
};

/** Private component to be used by all public modals. Represents the common internals of all modals */
BaseModalLayout.Header = Header;
BaseModalLayout.Content = Content;
BaseModalLayout.Footer = Footer;
BaseModalLayout.Footnote = Footnote;
BaseModalLayout.Illustration = Illustration;

BaseModalLayout.displayName = 'BaseModalLayout';

export default BaseModalLayout;
